import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ClipboardList, Plus, Trash2, CheckCircle2, Loader2, Eye, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const emptyItem = { medicine_name: "", quantity: 1, expected_price: 0 };

export default function PurchaseOrders() {
  const [suppliers, setSuppliers] = useState([]);
  const [pos, setPos] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [supplierId, setSupplierId] = useState("none");
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [fSupplier, setFSupplier] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);

  const load = () => {
    api.get("/purchase-orders").then(({ data }) => setPos(data)).catch(() => {});
    api.get("/suppliers").then(({ data }) => setSuppliers(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => setPage(0), [fSupplier, fStatus, search, pageSize]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return pos.filter((po) => {
      if (fSupplier !== "all" && po.supplier_id !== fSupplier) return false;
      if (fStatus !== "all" && po.status !== fStatus) return false;
      if (s && ![po.po_no, po.supplier_name, po.note, ...(po.items || []).map((i) => i.medicine_name)]
        .filter(Boolean).join(" ").toLowerCase().includes(s)) return false;
      return true;
    });
  }, [pos, fSupplier, fStatus, search]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pages - 1);
  const pageItems = filtered.slice(safePage * pageSize, (safePage + 1) * pageSize);

  const setItem = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));

  const create = async () => {
    const valid = items.filter((i) => i.medicine_name && i.quantity > 0);
    if (valid.length === 0) return toast.error("Add at least one item");
    setSaving(true);
    try {
      const { data } = await api.post("/purchase-orders", {
        supplier_id: supplierId === "none" ? "" : supplierId,
        items: valid.map((i) => ({ ...i, quantity: Number(i.quantity), expected_price: Number(i.expected_price) || 0 })),
        note,
      });
      toast.success(`PO ${data.po_no} created`);
      setItems([{ ...emptyItem }]);
      setNote("");
      setSupplierId("none");
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const receive = async (po) => {
    if (!window.confirm(`Receive PO ${po.po_no}? Stock will be added automatically.`)) return;
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/receive`);
      toast.success(`Received! ${data.medicines_stocked}/${data.total_items} medicines added to stock`);
      setDetail(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="purchase-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-emerald-700" /> Purchase Orders
          </h1>
          <p className="text-sm text-slate-500">{pos.length} orders • Suppliers ko order bhejein, stock aane par receive karein.</p>
        </div>
        <Button data-testid="po-new-btn" onClick={() => setCreateOpen(true)} className="bg-emerald-900 hover:bg-emerald-800">
          <Plus className="w-4 h-4 mr-2" /> New Purchase Order
        </Button>
      </div>

      <Card className="border border-slate-300 rounded-lg overflow-hidden">
        <div className="grid grid-cols-2 bg-slate-50 border-b border-slate-300">
          <div className="p-3 border-r border-slate-300">
            <Label className="text-xs text-slate-600">Supplier</Label>
            <Select value={fSupplier} onValueChange={setFSupplier}>
              <SelectTrigger data-testid="po-filter-supplier" className="mt-1 h-10 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="p-3">
            <Label className="text-xs text-slate-600">Status</Label>
            <Select value={fStatus} onValueChange={setFStatus}>
              <SelectTrigger data-testid="po-filter-status" className="mt-1 h-10 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Status All</SelectItem>
                <SelectItem value="ordered">Ordered</SelectItem>
                <SelectItem value="received">Received</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 border-b border-slate-300">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            Show
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger data-testid="po-page-size" className="h-8 w-[70px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            entries
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Search:</span>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="po-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="PO no / supplier / medicine" className="h-8 w-56 pl-8 bg-white" />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table data-testid="po-table" className="text-xs [&_th]:border [&_td]:border [&_th]:border-slate-300 [&_td]:border-slate-300 [&_th]:bg-slate-100 [&_th]:text-slate-700 [&_th]:font-bold [&_th]:py-2 [&_td]:py-1.5 [&_th]:px-2 [&_td]:px-2">
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>PO No</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead className="text-center">Items</TableHead>
                <TableHead className="text-right">Expected Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Received On</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((po, i) => (
                <TableRow key={po.id} data-testid={`po-row-${po.id}`} className="hover:bg-emerald-50/40">
                  <TableCell className="font-mono-num text-slate-500">{safePage * pageSize + i + 1}</TableCell>
                  <TableCell className="font-mono-num font-bold text-slate-900">{po.po_no}</TableCell>
                  <TableCell className="whitespace-nowrap">{new Date(po.created_at).toLocaleDateString("en-IN")}</TableCell>
                  <TableCell>{po.supplier_name || "—"}</TableCell>
                  <TableCell className="text-center">
                    <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 font-medium">{po.items.length}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono-num font-semibold">₹{po.expected_total.toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge data-testid={`po-status-${po.id}`} className={po.status === "received" ? "bg-emerald-600 text-white hover:bg-emerald-600 font-medium" : "bg-amber-400 text-amber-950 hover:bg-amber-400 font-medium"}>
                      {po.status === "received" ? "Received" : "Ordered"}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{po.received_at ? new Date(po.received_at).toLocaleDateString("en-IN") : "—"}</TableCell>
                  <TableCell className="max-w-[140px] truncate text-slate-500">{po.note || "—"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" data-testid={`po-open-${po.id}`} onClick={() => setDetail(po)} className="h-7 text-xs">
                      <Eye className="w-3.5 h-3.5 mr-1" /> Open
                    </Button>
                    {po.status !== "received" && (
                      <Button size="sm" data-testid={`po-receive-${po.id}`} onClick={() => receive(po)} className="h-7 text-xs ml-1 bg-emerald-900 hover:bg-emerald-800">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Receive
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {pageItems.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-400">No purchase orders found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 border-t border-slate-300 text-sm text-slate-600">
          <div data-testid="po-table-info">
            Showing {filtered.length === 0 ? 0 : safePage * pageSize + 1} to {Math.min((safePage + 1) * pageSize, filtered.length)} of {filtered.length} entries
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-8" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} data-testid="po-prev-page"><ChevronLeft className="w-4 h-4" /> Prev</Button>
            <span className="px-2 font-mono-num text-xs">{safePage + 1} / {pages}</span>
            <Button size="sm" variant="outline" className="h-8" disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)} data-testid="po-next-page">Next <ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg bg-white" data-testid="po-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ClipboardList className="w-5 h-5 text-emerald-700" /> New Purchase Order</DialogTitle>
            <DialogDescription className="text-xs">Supplier select karein, items add karein — receive par stock automatically badhega.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-xs">Supplier</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger data-testid="po-supplier" className="mt-1 h-10"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">— No supplier —</SelectItem>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Items</Label>
              {items.map((it, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input data-testid={`po-item-name-${i}`} value={it.medicine_name} onChange={(e) => setItem(i, "medicine_name", e.target.value)} placeholder="Medicine name" className="h-10 flex-1" />
                  <Input data-testid={`po-item-qty-${i}`} type="number" min="1" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="h-10 w-20" title="Strips" />
                  <Input data-testid={`po-item-price-${i}`} type="number" min="0" value={it.expected_price} onChange={(e) => setItem(i, "expected_price", e.target.value)} className="h-10 w-24" title="Price/strip" placeholder="₹" />
                  <Button size="icon" variant="ghost" onClick={() => setItems(items.filter((_, idx) => idx !== i))} disabled={items.length === 1}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" data-testid="po-add-item" onClick={() => setItems([...items, { ...emptyItem }])}>
                <Plus className="w-4 h-4 mr-1" /> Item
              </Button>
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 h-10" placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} data-testid="po-create-btn" className="bg-emerald-900 hover:bg-emerald-800">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ClipboardList className="w-4 h-4 mr-2" />}
              Create PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="po-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {detail?.po_no}
              {detail && (
                <Badge data-testid="po-detail-status" className={detail.status === "received" ? "bg-emerald-600 text-white hover:bg-emerald-600" : "bg-amber-400 text-amber-950 hover:bg-amber-400"}>
                  {detail.status === "received" ? "Received" : "Ordered"}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs">Order ka poora detail — kaunsi medicine, kitni qty, kis rate par.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="text-sm text-slate-600 space-y-0.5">
                <div>Supplier: <span className="font-semibold text-slate-900">{detail.supplier_name || "—"}</span></div>
                <div className="text-xs">Ordered: {new Date(detail.created_at).toLocaleString("en-IN")}</div>
                {detail.received_at && <div className="text-xs">Received: {new Date(detail.received_at).toLocaleString("en-IN")}</div>}
                {detail.note && <div className="text-xs italic">"{detail.note}"</div>}
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Order items — kya manga tha</div>
                <div className="border border-slate-300 rounded-lg overflow-hidden">
                  <Table className="text-xs [&_th]:border [&_td]:border [&_th]:border-slate-300 [&_td]:border-slate-300 [&_th]:bg-slate-100 [&_th]:py-1.5 [&_td]:py-1.5 [&_th]:px-2 [&_td]:px-2">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Medicine</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                        <TableHead className="text-right">Subtotal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.items.map((it, i) => (
                        <TableRow key={i} data-testid={`po-detail-item-${i}`}>
                          <TableCell className="font-medium">{it.medicine_name}</TableCell>
                          <TableCell className="text-right font-mono-num">{it.quantity}</TableCell>
                          <TableCell className="text-right font-mono-num">₹{Number(it.expected_price).toFixed(2)}</TableCell>
                          <TableCell className="text-right font-mono-num font-semibold">₹{Number(it.subtotal).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={3} className="text-right font-bold">Expected Total</TableCell>
                        <TableCell className="text-right font-mono-num font-bold" data-testid="po-detail-total">₹{detail.expected_total.toFixed(2)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)} data-testid="po-detail-close">Close</Button>
            {detail?.status !== "received" && (
              <Button onClick={() => receive(detail)} data-testid="po-detail-receive" className="bg-emerald-900 hover:bg-emerald-800">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Receive Stock
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
